import { SHStack } from "@components/design-systems/sh-stack";
import { SHBooleanField } from "@components/design-systems/sh-boolean-field";
import { Controller, useFormContext } from "react-hook-form";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { SelectRateCards } from "@components/auto-completes/rate-cards";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { compact, isEmpty, isEqual } from "lodash";
import { useParams } from "react-router";

export const AnalysisSetupStepRateCardsSelection = ({
  disabled,
}: AnalysisSetupStepRenderedFieldProps) => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const { control, watch, setValue } = useFormContext<AnalysisSetupDTO>();
  const { platformAnalysis } = useSelector(
    (state: RootState) => state.platformAnalysis,
  );

  const watchedHasMainProduct = watch("hasMainProduct");
  const watchedOtherProductIds = watch("otherProductIds");
  const watchedMainProductId = watch("mainProductId");
  const watchedHasRateCard = watch("hasRateCard");

  const allProductIds = useMemo(() => {
    const otherIds = watchedOtherProductIds || [];
    return watchedHasMainProduct && watchedMainProductId
      ? [watchedMainProductId, ...otherIds]
      : otherIds;
  }, [watchedHasMainProduct, watchedMainProductId, watchedOtherProductIds]);

  const isGetLastedConfig = useMemo(() => {
    if (isEmpty(allProductIds)) {
      setValue("hasRateCard", false);
      setValue("selectRateCards", []);
    }

    const initProductIds = [
      platformAnalysis?.setup?.mainProductId,
      ...(platformAnalysis?.setup?.otherProductIds ?? []),
    ];

    return !isEqual(
      compact(initProductIds).sort(),
      compact(allProductIds).sort(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProductIds]);

  return (
    <SHStack
      spacing={3}
      direction="column"
      sx={{ width: { xs: "100%", md: 520 } }}
    >
      <Controller
        name={"hasRateCard"}
        control={control}
        render={({ field: { onChange, ...other } }) => (
          <SHBooleanField
            {...other}
            isSwitch
            isShowClearButton={false}
            value={other.value}
            onChange={(_, value?: boolean | null) => {
              onChange(value);
              if (!value) {
                setValue("selectRateCards", [], {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
            }}
            label={"Include rate cards?"}
            disabled={disabled}
          />
        )}
      />

      {!!watchedHasRateCard && (
        <Controller
          name={"selectRateCards"}
          control={control}
          render={({ field: { onChange, ...other } }) => (
            <SelectRateCards
              {...other}
              configurationVersion={
                isGetLastedConfig
                  ? undefined
                  : platformAnalysis?.configurationVersion
              }
              disabled={disabled}
              analysisId={analysisId}
              productIds={allProductIds}
              onChange={(value) => {
                onChange(value || []);
              }}
              textFieldProps={{
                label: "Select rate cards to include in your analysis",
                placeholder: "Search for rate cards",
              }}
            />
          )}
        />
      )}
    </SHStack>
  );
};
